// A minimal application/x-www-form-urlencoded reader and writer for `viewState`.
//
// Written here rather than on `URLSearchParams` because @sl/core compiles against ES2022 only (no DOM lib)
// and React Native's `URLSearchParams` lacks `get` / `getAll`. Reading accepts whatever a browser or router
// hands over: a raw string (`?a=1&b=2`, `#/home?a=1`, `a=1`), anything with `getAll` / `get` (a
// `URLSearchParams`), or a plain record (Expo Router's params). Writing is canonical: keys sorted, values
// `encodeURIComponent`-encoded with `,` and `:` left readable, spaces as `%20`, never `+`.

export type ParamSource =
  | string
  | Iterable<readonly [string, string]> // a `URLSearchParams`, a `Map`, an array of pairs
  | { getAll(name: string): string[] }
  | { get(name: string): string | null }
  | Readonly<Record<string, string | readonly string[] | null | undefined>>;

/** Every value seen for each name (duplicates preserved, in order) plus the names whose value could not be
 *  percent-decoded (`malformed`) — those are reported, never thrown (a bad link must still open the app). */
export interface ReadParams {
  values: Map<string, string[]>;
  malformed: Set<string>;
}

function decode(raw: string): string | null {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return null;
  }
}

function readString(input: string, out: ReadParams): void {
  let s = input;
  const q = s.indexOf('?');
  if (q >= 0) s = s.slice(q + 1);
  else if (s.startsWith('#')) s = s.slice(1);
  const h = s.indexOf('#');
  if (h >= 0) s = s.slice(0, h);
  for (const part of s.split('&')) {
    if (part === '') continue;
    const eq = part.indexOf('=');
    const rawName = eq >= 0 ? part.slice(0, eq) : part;
    const rawValue = eq >= 0 ? part.slice(eq + 1) : '';
    const name = decode(rawName);
    if (name === null || name === '') continue;
    const value = decode(rawValue);
    if (value === null) {
      out.malformed.add(name);
      continue;
    }
    push(out.values, name, value);
  }
}

function push(values: Map<string, string[]>, name: string, value: string): void {
  const list = values.get(name);
  if (list) list.push(value);
  else values.set(name, [value]);
}

/** Read every parameter of `source`, whatever its shape. A string, an iterable of pairs (`URLSearchParams`)
 *  or a record yields everything; an object that only offers `get` / `getAll` cannot be enumerated, so only
 *  the names in `names` are looked up on it. */
export function readParams(source: ParamSource, names: readonly string[]): ReadParams {
  const out: ReadParams = { values: new Map(), malformed: new Set() };
  if (typeof source === 'string') {
    readString(source, out);
    return out;
  }
  if (typeof (source as { [Symbol.iterator]?: unknown })[Symbol.iterator] === 'function') {
    for (const entry of source as Iterable<readonly [string, string]>) {
      if (!Array.isArray(entry) || entry.length < 2) continue;
      push(out.values, String(entry[0]), String(entry[1]));
    }
    return out;
  }
  if (typeof (source as { getAll?: unknown }).getAll === 'function') {
    const s = source as { getAll(name: string): string[] };
    for (const name of names) for (const v of s.getAll(name)) push(out.values, name, v);
    return out;
  }
  if (typeof (source as { get?: unknown }).get === 'function') {
    const s = source as { get(name: string): string | null };
    for (const name of names) {
      const v = s.get(name);
      if (v !== null && v !== undefined) push(out.values, name, v);
    }
    return out;
  }
  for (const [name, v] of Object.entries(source as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    if (Array.isArray(v)) for (const item of v) push(out.values, name, String(item));
    else push(out.values, name, String(v));
  }
  return out;
}

function encode(s: string): string {
  return encodeURIComponent(s).replace(/%2C/gi, ',').replace(/%3A/gi, ':');
}

/** Canonical query string (no leading `?`): pairs sorted by name (stable for equal names), encoded once. */
export function writeParams(pairs: readonly (readonly [string, string])[]): string {
  return [...pairs]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([k, v]) => `${encode(k)}=${encode(v)}`)
    .join('&');
}
